import { motion } from 'framer-motion';
import Mission from '../components/about/Mission';
import ExcerptBlock from '../components/about/ExcerptBlock';
import TeamPointer from '../components/about/TeamPointer';
import PageTransition from '../components/common/PageTransition';
import { FaWhatsapp } from 'react-icons/fa';
import SEO from '../components/common/SEO';

const values = [
  { icon: '🎨', title: 'Creative', description: 'We celebrate imagination and encourage creative thinking in every story.', color: '#F57C00', bg: 'rgba(245,124,0,0.07)', border: 'rgba(245,124,0,0.15)' },
  { icon: '🌟', title: 'Quality', description: 'Every page is crafted with care to deliver the best content for young minds.', color: '#7B2FBE', bg: 'rgba(123,47,190,0.07)', border: 'rgba(123,47,190,0.15)' },
  { icon: '🤝', title: 'Inclusive', description: 'Content that resonates with children from all backgrounds and cultures.', color: '#1565C0', bg: 'rgba(21,101,192,0.07)', border: 'rgba(21,101,192,0.15)' },
  { icon: '💡', title: 'Educational', description: 'Learning disguised as fun — the best way to grow curious minds!', color: '#3CB044', bg: 'rgba(60,176,68,0.07)', border: 'rgba(60,176,68,0.15)' },
];

const funFacts = [
  { number: '35', label: 'Issues per Year', icon: '📅', color: '#7B2FBE' },
  { number: '100+', label: 'Puzzles Monthly', icon: '🧩', color: '#1565C0' },
  { number: '2', label: 'Age Editions', icon: '📚', color: '#3CB044' },
  { number: '1000+', label: 'Happy Readers', icon: '😊', color: '#F57C00' },
];

const About = () => {
  return (
    <PageTransition>
      <SEO
        title="About Our Weekly Magazine"
        description="Learn about the mission, values, and story behind The Tiny Times. We make learning irresistibly fun with weekly stories, activities, and puzzles."
        keywords="about tiny times, kids magazine mission, kids educational team, weekly stories children, early learning kids"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About The Tiny Times",
          "description": "Learn about the mission, values, and story behind The Tiny Times. We make learning irresistibly fun with weekly stories, activities, and puzzles.",
          "url": "https://the-tiny-times.web.app/about"
        }}
      />
      <div className="min-h-screen">

        {/* Hero Section */}
        <section
          className="relative py-24 md:py-36 overflow-hidden"
          style={{
            background: 'linear-gradient(160deg, #EDF7EE 0%, #E3F2FD 40%, #EDE7F6 75%, #FFF9E6 100%)',
          }}
        >
          {/* Background dots */}
          <div className="absolute inset-0 opacity-25 pointer-events-none"
            style={{
              backgroundImage: 'radial-gradient(circle, rgba(60,176,68,0.15) 1px, transparent 1px)',
              backgroundSize: '36px 36px',
            }}
          />
          {/* Decorative blobs */}
          <div className="absolute top-0 right-0 w-72 h-72 opacity-20 pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(123,47,190,0.3), transparent 70%)', filter: 'blur(50px)', transform: 'translate(20%,-20%)' }} />
          <div className="absolute bottom-0 left-0 w-72 h-72 opacity-15 pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(21,101,192,0.3), transparent 70%)', filter: 'blur(50px)', transform: 'translate(-20%,20%)' }} />

          {/* Floating emojis */}
          {[{ e: '📖', x: '8%', y: '20%' }, { e: '✏️', x: '88%', y: '25%' }, { e: '🌟', x: '12%', y: '70%' }, { e: '🎯', x: '85%', y: '68%' }].map((item, i) => (
            <motion.div key={i} className="absolute text-5xl opacity-20 pointer-events-none select-none"
              style={{ left: item.x, top: item.y }}
              animate={{ y: [0, -18, 0], rotate: [0, 10, -10, 0] }}
              transition={{ duration: 4 + i * 0.5, repeat: Infinity, delay: i * 0.3 }}>
              {item.e}
            </motion.div>
          ))}

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center"
            >
              <div className="inline-block mb-5 px-5 py-2 rounded-full font-display font-semibold text-sm"
                style={{ background: 'rgba(60,176,68,0.12)', color: '#1E7C24', border: '1px solid rgba(60,176,68,0.25)' }}>
                📖 Our Story
              </div>
              <h1 className="text-5xl md:text-7xl font-display font-bold mb-6 leading-tight"
                style={{ color: '#1A1F2E' }}>
                About{' '}
                <span style={{
                  background: 'linear-gradient(135deg, #3CB044, #1565C0)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  The Tiny Times
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-neutral-gray-dark leading-relaxed">
                Where learning meets fun, and every page is an adventure! 🚀
              </p>
            </motion.div>
          </div>

          {/* Wave */}
          <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
            <svg viewBox="0 0 1440 80" className="w-full" preserveAspectRatio="none" style={{ display: 'block' }}>
              <path fill="white" d="M0,40L60,45C120,50,240,60,360,55C480,50,600,30,720,28C840,25,960,35,1080,40C1200,45,1320,42,1380,40L1440,38L1440,80L0,80Z" />
            </svg>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-16 md:py-24 bg-white">
          <Mission />
        </section>

        {/* Our Story */}
        <section className="py-16 md:py-24" style={{ background: 'linear-gradient(160deg, #F0FFF4, #EFF6FF)' }}>
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto"
            >
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>Our Story 📚</h2>
                <div className="mx-auto w-24 h-1 rounded-full" style={{ background: 'linear-gradient(90deg, #3CB044, #1565C0)' }} />
              </div>

              <motion.div
                whileHover={{ y: -4 }}
                className="rounded-2xl p-8 md:p-10"
                style={{ background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(16px)', boxShadow: '0 8px 40px rgba(0,0,0,0.08)', border: '1px solid rgba(255,255,255,0.9)' }}>
                <div className="space-y-4 text-lg text-neutral-gray-dark leading-relaxed">
                  <p>
                    The Tiny Times was born from a simple idea:{' '}
                    <strong style={{ color: '#1E7C24' }}>make learning irresistibly fun!</strong> 🎯
                  </p>
                  <p>
                    Every week, we craft stories that spark imagination, puzzles that challenge young minds,
                    and activities that bring joy to children aged 4 to 15. Our magazine isn't just about
                    reading — it's about{' '}
                    <em style={{ color: '#F57C00', fontStyle: 'normal', fontWeight: '600' }}>experiencing the wonder of learning</em>.
                  </p>
                  <p>
                    From colorful adventures for our youngest readers to brain-teasing challenges for teens,
                    The Tiny Times is where curiosity meets creativity! 🌈
                  </p>
                  <p>
                    Published in <strong style={{ color: '#1565C0' }}>English with Telugu elements</strong>, our magazine bridges languages and cultures,
                    making it truly special for families across India. Based in <strong style={{ color: '#7B2FBE' }}>Hyderabad</strong>, delivering nationwide!
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>
                What We Believe In 💡
              </h2>
              <p className="text-lg text-neutral-gray-dark max-w-2xl mx-auto">Our core values guide everything we create</p>
              <div className="mt-4 mx-auto w-20 h-1 rounded-full" style={{ background: 'linear-gradient(90deg, #F57C00, #7B2FBE)' }} />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -10, scale: 1.03 }}
                  className="rounded-2xl p-6 text-center"
                  style={{ background: value.bg, border: `1px solid ${value.border}`, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
                >
                  <div className="text-6xl mb-4">{value.icon}</div>
                  <h3 className="text-xl font-display font-bold mb-3" style={{ color: value.color }}>{value.title}</h3>
                  <p className="text-sm text-neutral-gray-dark leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Fun Facts */}
        <section className="py-16 md:py-24" style={{ background: 'linear-gradient(160deg, #F5F0FF, #FFF8E1)' }}>
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>Fun Facts About Us! 🎉</h2>
            </motion.div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
              {funFacts.map((fact, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, type: 'spring', stiffness: 200 }}
                  whileHover={{ y: -8, scale: 1.04 }}
                  className="text-center rounded-2xl p-6"
                  style={{ background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.9)', boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}
                >
                  <div className="text-4xl mb-2">{fact.icon}</div>
                  <div className="text-4xl md:text-5xl font-display font-bold mb-1" style={{ color: fact.color }}>{fact.number}</div>
                  <div className="text-sm font-display font-semibold text-neutral-gray-dark">{fact.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Excerpt Block */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>Peek Inside Our Pages 👀</h2>
              <p className="text-lg text-neutral-gray-dark max-w-2xl mx-auto">Sample content from our latest editions</p>
            </motion.div>
            <div className="max-w-6xl mx-auto">
              <ExcerptBlock />
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-16 md:py-24" style={{ background: 'linear-gradient(160deg, #EDF7EE, #EFF6FF)' }}>
          <TeamPointer />
        </section>

        {/* CTA */}
        <section className="py-20" style={{ background: 'linear-gradient(135deg, #1E7C24, #145218)' }}>
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <div className="text-7xl mb-5">🚀</div>
              <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-5">Ready to Join the Adventure?</h2>
              <p className="text-xl text-white/85 mb-8">Subscribe today and become part of The Tiny Times family!</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="https://wa.me/919618377750"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-button font-display font-bold text-xl text-white"
                  style={{ background: '#25D366', boxShadow: '0 6px 24px rgba(37,211,102,0.4)' }}
                >
                  <FaWhatsapp className="text-2xl" /> Subscribe Now 📬
                </motion.a>
                <motion.a
                  href="/contact"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-button font-display font-bold text-xl"
                  style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '2px solid rgba(255,255,255,0.3)', backdropFilter: 'blur(8px)' }}
                >
                  Contact Us 💬
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>

      </div>
    </PageTransition>
  );
};

export default About;